import { Link } from "react-router-dom";
import RichText from "@/components/RichText";
import { openCookiePreferences, useCookieBannerEnabled } from "@/lib/consent";
import { CONTAINER } from "../tokens";

// Politique de confidentialité (/confidentialite) du moteur "agence_v1" —
// composée avec le vocabulaire du handoff ClickExpert : pastille d'accroche
// à point vert, titre grotesque serré, corps en `ink/[0.6]`, boutons
// contour arrondis `rounded-btn`. Une section par bloc "privacy_block". Le
// contenu vient du Studio (page « Politique de confidentialité »), voir
// backend/services/theme_engines/shared_pages.py.
const DEFAULTS = {
  privacy_eyebrow: "RGPD",
  privacy_heading: "Politique de confidentialité",
  privacy_intro: "",
  privacy_cookie_button_label: "Gérer mes préférences de cookies",
  privacy_back_label: "Retour à l'accueil",
  privacy_back_url: "/",
};

const BTN =
  "inline-flex items-center font-display font-medium text-[14px] border-control border-ink/[0.16] text-ink py-[12px] px-5 rounded-btn hover:border-ink/40 hover:bg-ink/5 transition-colors";

const isInternal = (url = "") => url.startsWith("/") && !url.startsWith("//");

export default function PrivacyContentSection({ props = {}, blocks = [] }) {
  const t = { ...DEFAULTS, ...props };
  const items = (blocks || []).filter((b) => b.type === "privacy_block" && b.enabled !== false);
  const bannerEnabled = useCookieBannerEnabled();

  return (
    <section className={`${CONTAINER} pt-[64px] pb-[92px]`}>
      <div className="max-w-[760px]">
        {t.privacy_eyebrow && (
          <div className="inline-flex items-center gap-[9px] border-control border-ink/[0.16] rounded-full py-[7px] px-[14px] font-display text-[12.5px] tracking-[0.06em] uppercase text-ink/70">
            <span className="h-1.5 w-1.5 rounded-full bg-blush shrink-0" />
            <RichText html={t.privacy_eyebrow} />
          </div>
        )}
        <RichText
          as="h1"
          className="block font-display font-semibold text-[clamp(32px,4.2vw,52px)] leading-[1.06] tracking-[-0.035em] mt-6 text-ink [text-wrap:pretty]"
          html={t.privacy_heading}
        />
        {t.privacy_intro && (
          <RichText
            as="div"
            preserveParagraphs
            className="text-[17px] leading-[1.6] text-ink/[0.66] mt-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:underline [&_a]:text-gold [&_a:hover]:text-copper"
            html={t.privacy_intro}
          />
        )}

        <div className="grid gap-9 mt-12">
          {items.map((b) => (
            <div key={b.id} data-block-key={b.id}>
              <RichText
                as="h2"
                className="block font-display font-semibold text-[21px] tracking-[-0.015em] text-ink mb-2.5"
                html={b.props?.title}
              />
              <RichText
                as="div"
                preserveParagraphs
                className="text-[15.5px] leading-[1.65] text-ink/[0.6] [&_p]:mb-2.5 [&_p:last-child]:mb-0 [&_a]:underline [&_a]:text-gold [&_a:hover]:text-copper"
                html={b.props?.body}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mt-11">
          {bannerEnabled && t.privacy_cookie_button_label && (
            <button type="button" onClick={openCookiePreferences} className={BTN} data-testid="privacy-cookie-prefs">
              {t.privacy_cookie_button_label}
            </button>
          )}
          {t.privacy_back_label && (
            isInternal(t.privacy_back_url || "/") ? (
              <Link to={t.privacy_back_url || "/"} className={BTN}>{t.privacy_back_label}</Link>
            ) : (
              <a href={t.privacy_back_url} className={BTN}>{t.privacy_back_label}</a>
            )
          )}
        </div>
      </div>
    </section>
  );
}
